import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { Link } from "react-router-dom";
import { axiosInstance, buildLegacyHeaders } from "../axiosFetchers";
import { useAuth } from "../Auth/useAuth";
import { WATER_LABELS, WATER_HELP, ENERGY_HELP, ENERGY_SOURCE_UNITS, ENERGY_UNITS, energySourceChange, irrigationErrors } from "../../utils/irrigation";
import {
  formatAcres,
  formatAcresForInput,
} from "../../utils/acreageFormatting.js";
import {
  buildGhgResultsCsv,
  GHG_CSV_TEMPLATE,
  parseGhgCalculatorCsv,
} from "./ghgQuickCalculatorCsv";

const API_ROOT = "/utilities/ghg-calculator";
const TERMINAL = new Set(["COMPLETED", "FAILED", "REJECTED_UNSUPPORTED", "EXPIRED", "DELETED"]);
const CROPS = ["CANOLA", "CORN", "SOYBEANS", "SPRING_WHEAT"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const YIELD_UNITS = ["bu/ac", "kg/ha", "t/ha", "tonne/ha", "tonnes/ha"];
const RATE_UNITS = ["kg N/ha", "kg P/ha", "kg/ha", "lb N/ac", "lb P/ac", "lb/ac"];
const WATER_UNITS = Object.keys(WATER_LABELS);

const DEFAULT_FORM = {
  contractVersion: "2.0",
  clientReference: "",
  cropYear: new Date().getFullYear(),
  crop: "CANOLA",
  acres: "",
  location: {
    provinceCode: "SK",
    latitude: "",
    longitude: "",
    postalCode: "",
    municipality: "",
  },
  production: {
    yield: "",
    yieldUnit: "bu/ac",
    tillagePractice: "",
    previousCrop: "",
    residueManagement: "",
    soilOrganicMatterPercent: "",
  },
  fertilizers: [],
  operations: [],
  irrigation: null,
};

const EMPTY_FERTILIZER = {
  productName: "",
  applicationRate: "",
  rateUnit: "lb/ac",
  nitrogenPercent: "0",
  phosphorusPercent: "0",
  potassiumPercent: "0",
  applicationDate: "",
  applicationMethod: "",
  hasNitrificationInhibitor: "",
};

const EMPTY_OPERATION = {
  operationType: "",
  operationDate: "",
  equipmentType: "",
  fuelUsedLitres: "",
};

const EMPTY_IRRIGATION = {
  waterApplied: "",
  waterUnit: "mm",
  energySource: "",
  energyUsed: "",
  energyUnit: "",
};

function apiError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join("; ") || fallback;
  return detail?.message || error?.message || fallback;
}

function downloadText(content, filename, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function numberOrNull(value) {
  return String(value ?? "").trim() === "" ? null : Number(value);
}

function formPayload(form) {
  return {
    contractVersion: "2.0",
    clientReference: form.clientReference.trim(),
    cropYear: Number(form.cropYear),
    crop: form.crop,
    acres: Number(form.acres),
    location: {
      provinceCode: form.location.provinceCode,
      latitude: numberOrNull(form.location.latitude),
      longitude: numberOrNull(form.location.longitude),
      postalCode: form.location.postalCode.trim() || null,
      municipality: form.location.municipality.trim() || null,
    },
    production: {
      yield: Number(form.production.yield),
      yieldUnit: form.production.yieldUnit,
      tillagePractice: form.production.tillagePractice || null,
      previousCrop: form.production.previousCrop.trim() || null,
      residueManagement: form.production.residueManagement.trim() || null,
      soilOrganicMatterPercent: numberOrNull(form.production.soilOrganicMatterPercent),
    },
    fertilizers: form.fertilizers.map((item) => ({
      productName: item.productName.trim(),
      applicationRate: Number(item.applicationRate),
      rateUnit: item.rateUnit,
      nitrogenPercent: Number(item.nitrogenPercent),
      phosphorusPercent: Number(item.phosphorusPercent),
      potassiumPercent: Number(item.potassiumPercent),
      applicationDate: item.applicationDate || null,
      applicationMethod: item.applicationMethod.trim() || null,
      hasNitrificationInhibitor: item.hasNitrificationInhibitor === "" ? null : item.hasNitrificationInhibitor === "true",
    })),
    operations: form.operations.map((item) => ({
      operationType: item.operationType.trim(),
      operationDate: item.operationDate || null,
      equipmentType: item.equipmentType.trim() || null,
      fuelUsedLitres: numberOrNull(item.fuelUsedLitres),
    })),
    irrigation: form.irrigation ? {
      waterApplied: Number(form.irrigation.waterApplied),
      waterUnit: form.irrigation.waterUnit,
      energySource: form.irrigation.energySource.trim() || null,
      energyUsed: numberOrNull(form.irrigation.energyUsed),
      energyUnit: form.irrigation.energyUnit.trim() || null,
    } : null,
  };
}

function formatNumber(value, maximumFractionDigits = 2) {
  if (value == null || !Number.isFinite(Number(value))) return "Not returned";
  return new Intl.NumberFormat("en-CA", { maximumFractionDigits }).format(Number(value));
}

function delay(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function statusTone(status) {
  if (status === "COMPLETED") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (["FAILED", "REJECTED_UNSUPPORTED", "EXPIRED"].includes(status)) return "bg-red-50 text-red-800 border-red-200";
  return "bg-amber-50 text-amber-800 border-amber-200";
}

function SupportTag({ type }) {
  const styles = {
    modeled: "border-emerald-200 bg-emerald-50 text-emerald-800",
    accepted: "border-amber-200 bg-amber-50 text-amber-900",
    informational: "border-slate-200 bg-slate-50 text-slate-600",
  };
  const labels = { modeled: "Modeled now", accepted: "Future-ready", informational: "Informational" };
  return <span className={`rounded-[4px] border px-2 py-1 text-[11px] font-bold uppercase ${styles[type]}`}>{labels[type]}</span>;
}

function SectionHeading({ title, description, tags, action }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-black text-slate-900">{title}</h2>
          {tags.map((tag) => <SupportTag key={tag} type={tag} />)}
        </div>
        <p className="mt-1 max-w-[760px] text-sm leading-6 text-slate-600">{description}</p>
      </div>
      {action}
    </div>
  );
}

function Metric({ label, value, unit }) {
  const hasValue = value != null && Number.isFinite(Number(value));
  return (
    <div className="min-w-0 border-l-2 border-[#2f6f4e] pl-3">
      <div className="text-xs font-bold uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-black text-slate-900">
        {hasValue ? `${formatNumber(value)} ${unit}` : "Not returned"}
      </div>
    </div>
  );
}

function JobResult({ job, onDownloadJson }) {
  const emissions = job.result?.emissions;
  const summary = job.inputSummary || {};
  return (
    <article className="rounded-[8px] border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.05)]">
      <header className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-black text-slate-900">{job.clientReference}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {summary.crop} · {summary.cropYear} · {formatAcres(summary.acres)} acres · {summary.fertilizers?.length || 0} fertilizer application{summary.fertilizers?.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-[4px] border px-2 py-1 text-xs font-bold ${statusTone(job.status)}`}>{job.status.replaceAll("_", " ")}</span>
          {job.status === "COMPLETED" ? (
            <Tooltip title="Download result JSON">
              <button aria-label={`Download ${job.clientReference} JSON`} className="inline-flex h-9 w-9 items-center justify-center rounded-[4px] border border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => onDownloadJson(job)} type="button">
                <DownloadOutlinedIcon fontSize="small" />
              </button>
            </Tooltip>
          ) : null}
        </div>
      </header>

      {TERMINAL.has(job.status) && job.status !== "COMPLETED" ? (
        <div className="px-5 py-4 text-sm text-red-700">{job.error || "This calculation could not be completed."}</div>
      ) : emissions ? (
        <div className="grid grid-cols-1 gap-5 px-5 py-5 md:grid-cols-3">
          <Metric label="Total emissions" unit="kg CO2e" value={emissions.totalKgCo2e} />
          <Metric label="Per acre" unit="kg CO2e/ac" value={emissions.kgCo2ePerAcre} />
          <Metric label="Per hectare" unit="kg CO2e/ha" value={emissions.kgCo2ePerHectare} />
          <Metric label="Carbon dioxide" unit="kg CO2e" value={emissions.carbonDioxideKgCo2e} />
          <Metric label="Nitrous oxide" unit="kg CO2e" value={emissions.nitrousOxideKgCo2e} />
          <Metric label="Soil carbon change" unit="kg CO2e" value={emissions.soilCarbonChangeKgCo2e} />
        </div>
      ) : (
        <div className="px-5 py-5">
          <LinearProgress color="success" />
          <p className="mt-3 text-sm text-slate-600">Waiting for the Holos calculation worker.</p>
        </div>
      )}

      {job.status === "COMPLETED" ? (
        <>
          {job.warnings?.length ? <div className="border-t border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900">{job.warnings.map((warning) => warning.message || warning.code).filter(Boolean).join(" ")}</div> : null}
          <footer className="border-t border-slate-200 bg-[#fcfcfa] px-5 py-3 text-xs text-slate-500">
            {job.modelName || "Holos"} {job.modelVersion || ""}
            {job.wrapperName ? ` · Produced by ${job.wrapperName}${job.wrapperVersion ? ` ${job.wrapperVersion}` : ""}` : ""}
            {job.calculatedUtc ? ` · ${new Date(job.calculatedUtc).toLocaleString("en-CA")}` : ""}
          </footer>
        </>
      ) : null}
    </article>
  );
}

function FertilizerRow({ item, index, rateUnits, onChange, onRemove }) {
  return (
    <div className="rounded-[8px] border border-slate-200 bg-[#fcfcfa] p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-black text-slate-800">Application {index + 1}</h3>
        <Tooltip title="Remove fertilizer application">
          <button aria-label={`Remove fertilizer application ${index + 1}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[4px] text-slate-500 hover:bg-red-50 hover:text-red-700" onClick={onRemove} type="button"><DeleteOutlineIcon fontSize="small" /></button>
        </Tooltip>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <TextField required label="Product name" value={item.productName} onChange={(event) => onChange("productName", event.target.value)} inputProps={{ maxLength: 150 }} />
        <TextField required label="Application rate" type="number" value={item.applicationRate} onChange={(event) => onChange("applicationRate", event.target.value)} inputProps={{ min: 0, max: 10000, step: "any" }} />
        <TextField required select label="Rate unit" value={item.rateUnit} onChange={(event) => onChange("rateUnit", event.target.value)}>{rateUnits.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
        <TextField required label="Nitrogen %" type="number" value={item.nitrogenPercent} onChange={(event) => onChange("nitrogenPercent", event.target.value)} inputProps={{ min: 0, max: 100, step: "any" }} />
        <TextField required label="Phosphorus %" type="number" value={item.phosphorusPercent} onChange={(event) => onChange("phosphorusPercent", event.target.value)} inputProps={{ min: 0, max: 100, step: "any" }} />
        <TextField required label="Potassium %" type="number" value={item.potassiumPercent} onChange={(event) => onChange("potassiumPercent", event.target.value)} inputProps={{ min: 0, max: 100, step: "any" }} helperText="Accepted for future Holos support" />
        <TextField label="Application date" type="date" value={item.applicationDate} onChange={(event) => onChange("applicationDate", event.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Application method" value={item.applicationMethod} onChange={(event) => onChange("applicationMethod", event.target.value)} inputProps={{ maxLength: 100 }} />
        <TextField select label="Nitrification inhibitor" value={item.hasNitrificationInhibitor} onChange={(event) => onChange("hasNitrificationInhibitor", event.target.value)}>
          <MenuItem value="">Not provided</MenuItem><MenuItem value="true">Yes</MenuItem><MenuItem value="false">No</MenuItem>
        </TextField>
      </div>
    </div>
  );
}

function OperationRow({ item, index, onChange, onRemove }) {
  return (
    <div className="rounded-[8px] border border-slate-200 bg-[#fcfcfa] p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-black text-slate-800">Operation {index + 1}</h3>
        <Tooltip title="Remove field operation">
          <button aria-label={`Remove field operation ${index + 1}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[4px] text-slate-500 hover:bg-red-50 hover:text-red-700" onClick={onRemove} type="button"><DeleteOutlineIcon fontSize="small" /></button>
        </Tooltip>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <TextField required label="Operation type" value={item.operationType} onChange={(event) => onChange("operationType", event.target.value)} inputProps={{ maxLength: 100 }} />
        <TextField label="Operation date" type="date" value={item.operationDate} onChange={(event) => onChange("operationDate", event.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Equipment type" value={item.equipmentType} onChange={(event) => onChange("equipmentType", event.target.value)} inputProps={{ maxLength: 100 }} />
        <TextField label="Fuel used (litres)" type="number" value={item.fuelUsedLitres} onChange={(event) => onChange("fuelUsedLitres", event.target.value)} inputProps={{ min: 0, max: 10000, step: "any" }} />
      </div>
    </div>
  );
}

export default function GhgQuickCalculatorUtility({ onBack }) {
  const { user } = useAuth();
  const [mode, setMode] = useState("form");
  const [capabilities, setCapabilities] = useState(null);
  const [capabilityError, setCapabilityError] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [isAcresFocused, setIsAcresFocused] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [csvRows, setCsvRows] = useState([]);
  const [csvError, setCsvError] = useState("");
  const cancelled = useRef(false);
  const headers = useMemo(() => buildLegacyHeaders(user), [user]);

  useEffect(() => {
    cancelled.current = false;
    axiosInstance.get(`${API_ROOT}/capabilities`, { headers }).then((response) => {
      if (!cancelled.current) setCapabilities(response.data);
    }).catch((error) => {
      if (!cancelled.current) setCapabilityError(apiError(error, "Could not load calculator capabilities."));
    });
    return () => { cancelled.current = true; };
  }, [headers]);

  const upsertJob = useCallback((nextJob) => {
    setJobs((current) => current.some((job) => job.jobId === nextJob.jobId)
      ? current.map((job) => (job.jobId === nextJob.jobId ? nextJob : job))
      : [...current, nextJob]);
  }, []);

  const pollJob = useCallback(async (initialJob) => {
    let job = initialJob;
    while (!cancelled.current && !TERMINAL.has(job.status)) {
      await delay((job.pollAfterSeconds || 5) * 1000);
      if (cancelled.current) break;
      try {
        const response = await axiosInstance.get(`${API_ROOT}/jobs/${job.jobId}`, { headers });
        job = response.data;
        upsertJob(job);
      } catch (error) {
        job = { ...job, status: error?.response?.status === 410 ? "EXPIRED" : "FAILED", error: apiError(error, "Unable to retrieve this calculation.") };
        upsertJob(job);
      }
    }
    return job;
  }, [headers, upsertJob]);

  const submitPayload = useCallback(async (payload) => {
    const idempotencyKey = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    const response = await axiosInstance.post(`${API_ROOT}/jobs`, payload, { headers: { ...headers, "Idempotency-Key": idempotencyKey } });
    upsertJob(response.data);
    const completedJob = await pollJob(response.data);
    if (completedJob.status !== "COMPLETED") {
      throw new Error(
        `${payload.clientReference}: ${completedJob.error || `Calculation ended with ${completedJob.status.replaceAll("_", " ")}.`}`
      );
    }
    return completedJob;
  }, [headers, pollJob, upsertJob]);

  async function handleFormSubmit(event) {
    event.preventDefault();
    const hasLatitude = String(form.location.latitude).trim() !== "";
    const hasLongitude = String(form.location.longitude).trim() !== "";
    if (hasLatitude !== hasLongitude) {
      setMessage("Latitude and longitude must be supplied together.");
      return;
    }
    if (form.irrigation) {
      const errors = irrigationErrors(form.irrigation, { complete: true, acres: form.acres });
      if (Object.keys(errors).length) {
        setMessage(Object.values(errors).join(" "));
        return;
      }
    }
    setBusy(true);
    setMessage("");
    try {
      await submitPayload(formPayload(form));
      setMessage(`Calculation completed. Results remain available for ${capabilities?.resultTtlHours || 24} hours.`);
    } catch (error) {
      setMessage(apiError(error, "Could not submit the calculation."));
    } finally {
      setBusy(false);
    }
  }

  async function handleCsvFile(file) {
    setCsvError("");
    setCsvRows([]);
    if (!file) return;
    if (file.size > (capabilities?.maxCsvBytes || 256 * 1024)) {
      setCsvError("The CSV exceeds the 256 KiB limit.");
      return;
    }
    try {
      setCsvRows(parseGhgCalculatorCsv(await file.text(), capabilities));
    } catch (error) {
      setCsvError(error.message || "Could not parse that CSV.");
    }
  }

  async function handleCsvSubmit() {
    if (!csvRows.length || csvRows.some((row) => row.errors.length)) return;
    setBusy(true);
    setMessage("");
    try {
      for (const row of csvRows) {
        if (cancelled.current) break;
        await submitPayload(row.payload);
      }
      setMessage(`${csvRows.length} calculation${csvRows.length === 1 ? "" : "s"} completed.`);
    } catch (error) {
      setMessage(apiError(error, "CSV processing stopped before all rows completed."));
    } finally {
      setBusy(false);
    }
  }

  function updateRoot(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function updateGroup(group, name, value) {
    setForm((current) => ({ ...current, [group]: { ...current[group], [name]: value } }));
  }

  function updateRow(group, index, name, value) {
    setForm((current) => ({ ...current, [group]: current[group].map((row, rowIndex) => rowIndex === index ? { ...row, [name]: value } : row) }));
  }

  function removeRow(group, index) {
    setForm((current) => ({ ...current, [group]: current[group].filter((_, rowIndex) => rowIndex !== index) }));
  }

  function downloadJob(job) {
    downloadText(JSON.stringify(job, null, 2), `${job.clientReference}-ghg-result.json`, "application/json");
  }

  async function clearResults() {
    await Promise.allSettled(jobs.map((job) => axiosInstance.delete(`${API_ROOT}/jobs/${job.jobId}`, { headers })));
    setJobs([]);
    setMessage("");
  }

  const hasCsvErrors = csvRows.some((row) => row.errors.length);
  const completedJobs = jobs.filter((job) => job.status === "COMPLETED");
  const crops = capabilities?.supportedCrops || CROPS;
  const provinces = capabilities?.provinceCodes || PROVINCES;
  const yieldUnits = capabilities?.yieldUnits || YIELD_UNITS;
  const rateUnits = capabilities?.fertilizerRateUnits || RATE_UNITS;
  const waterUnits = capabilities?.irrigationWaterUnits || WATER_UNITS;

  return (
    <div className="min-h-full w-full bg-[#f7f8f5] px-4 py-5 md:px-8 md:py-7">
      <div className="mx-auto max-w-[1240px]">
        <header className="border-b border-slate-200 pb-6">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-500"><Link className="hover:text-[#237a43]" to="/utilities">Utilities</Link><span>/</span><span>Analysis</span></div>
          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="text-xs font-bold uppercase text-[#237a43]">Complete Holos request</div>
              <h1 className="mt-2 text-[34px] font-black leading-tight text-slate-950 md:text-[44px]">GHG Quick Calculator</h1>
              <p className="mt-3 max-w-[800px] text-base leading-7 text-slate-600">Build a field-level Holos estimate without creating farm, field, or crop-year records.</p>
            </div>
            <button className="inline-flex items-center gap-2 self-start rounded-[4px] border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50" onClick={onBack} type="button"><ArrowBackIcon fontSize="small" /> Back to utilities</button>
          </div>
        </header>

        <div className="mt-5 flex flex-wrap gap-2 border-l-4 border-[#237a43] bg-white px-4 py-3 text-sm text-slate-700">
          <span className="font-bold">Fieldprint calculator contract 2.0</span><span className="text-slate-400">|</span><span>Green fields affect the current model. Amber fields are retained for future model support. Grey fields are informational.</span>
        </div>
        {capabilityError ? <Alert className="mt-4" severity="error">{capabilityError}</Alert> : null}
        {capabilities && !capabilities.available ? <Alert className="mt-4" severity="warning">The calculator is currently unavailable.</Alert> : null}

        <div className="mt-6 inline-flex rounded-[6px] border border-slate-300 bg-white p-1" role="tablist" aria-label="Calculator input mode">
          {[["form", "Calculation form"], ["csv", "CSV batch"]].map(([value, label]) => <button aria-selected={mode === value} className={`rounded-[4px] px-4 py-2 text-sm font-bold ${mode === value ? "bg-[#243245] text-white" : "text-slate-600 hover:bg-slate-50"}`} key={value} onClick={() => setMode(value)} role="tab" type="button">{label}</button>)}
        </div>

        {mode === "form" ? (
          <form className="mt-4 overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.05)]" onSubmit={handleFormSubmit}>
            <section className="p-5 md:p-6">
              <SectionHeading title="Calculation" tags={["modeled"]} description="Identify this transient result and define the production year, crop, and field area." />
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <TextField required label="Reference" value={form.clientReference} onChange={(event) => updateRoot("clientReference", event.target.value)} inputProps={{ maxLength: 64 }} helperText="Used only to label this result" />
                <TextField required label="Crop year" type="number" value={form.cropYear} onChange={(event) => updateRoot("cropYear", event.target.value)} inputProps={{ min: 1900, max: 2200 }} />
                <TextField required select label="Crop" value={form.crop} onChange={(event) => updateRoot("crop", event.target.value)}>{crops.map((value) => <MenuItem key={value} value={value}>{value.replaceAll("_", " ")}</MenuItem>)}</TextField>
                <TextField required label="Field acres" type="number" value={isAcresFocused ? form.acres : formatAcresForInput(form.acres)} onBlur={() => setIsAcresFocused(false)} onFocus={() => setIsAcresFocused(true)} onChange={(event) => updateRoot("acres", event.target.value)} inputProps={{ min: 0.01, max: 1000000, step: "any" }} />
              </div>
            </section>

            <section className="border-t border-slate-200 p-5 md:p-6">
              <SectionHeading title="Location" tags={["modeled", "informational"]} description="Province and coordinates select the Holos regional context. Municipality and postal code are retained as informational metadata." />
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <TextField required select label="Province or territory" value={form.location.provinceCode} onChange={(event) => updateGroup("location", "provinceCode", event.target.value)}>{provinces.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
                <TextField label="Latitude" type="number" value={form.location.latitude} onChange={(event) => updateGroup("location", "latitude", event.target.value)} inputProps={{ min: -90, max: 90, step: "any" }} helperText="Optional, but use both coordinates together" />
                <TextField label="Longitude" type="number" value={form.location.longitude} onChange={(event) => updateGroup("location", "longitude", event.target.value)} inputProps={{ min: -180, max: 180, step: "any" }} />
                <TextField label="Municipality" value={form.location.municipality} onChange={(event) => updateGroup("location", "municipality", event.target.value)} inputProps={{ maxLength: 100 }} />
                <TextField label="Postal code" value={form.location.postalCode} onChange={(event) => updateGroup("location", "postalCode", event.target.value)} inputProps={{ maxLength: 20 }} />
              </div>
            </section>

            <section className="border-t border-slate-200 p-5 md:p-6">
              <SectionHeading title="Production and soil" tags={["modeled", "accepted"]} description="Yield, No Till, and soil organic matter are modeled today. Previous crop and residue are preserved for future Holos mapping." />
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <TextField required label="Yield" type="number" value={form.production.yield} onChange={(event) => updateGroup("production", "yield", event.target.value)} inputProps={{ min: 0, max: 100000, step: "any" }} />
                <TextField required select label="Yield unit" value={form.production.yieldUnit} onChange={(event) => updateGroup("production", "yieldUnit", event.target.value)}>{yieldUnits.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
                <TextField select label="Tillage practice" value={form.production.tillagePractice} onChange={(event) => updateGroup("production", "tillagePractice", event.target.value)} helperText="Blank and No Till are the only supported values"><MenuItem value="">Use Holos default</MenuItem><MenuItem value="NoTill">No Till</MenuItem></TextField>
                <TextField label="Soil organic matter (%)" type="number" value={form.production.soilOrganicMatterPercent} onChange={(event) => updateGroup("production", "soilOrganicMatterPercent", event.target.value)} inputProps={{ min: 0, max: 100, step: "any" }} />
                <TextField label="Previous crop" value={form.production.previousCrop} onChange={(event) => updateGroup("production", "previousCrop", event.target.value)} inputProps={{ maxLength: 100 }} />
                <TextField label="Residue management" value={form.production.residueManagement} onChange={(event) => updateGroup("production", "residueManagement", event.target.value)} inputProps={{ maxLength: 100 }} />
              </div>
            </section>

            <section className="border-t border-slate-200 p-5 md:p-6">
              <SectionHeading title="Fertilizer applications" tags={["modeled", "accepted"]} description="Holos currently models product rate and nitrogen/phosphorus composition. Potassium, date, method, and inhibitor are retained for future support." action={<button className="inline-flex items-center gap-2 self-start rounded-[4px] border border-[#237a43] px-3 py-2 text-sm font-bold text-[#237a43] hover:bg-emerald-50" disabled={form.fertilizers.length >= (capabilities?.maxFertilizers || 25)} onClick={() => setForm((current) => ({ ...current, fertilizers: [...current.fertilizers, { ...EMPTY_FERTILIZER }] }))} type="button"><AddOutlinedIcon fontSize="small" /> Add fertilizer</button>} />
              <div className="mt-5 space-y-4">
                {form.fertilizers.length ? form.fertilizers.map((item, index) => <FertilizerRow index={index} item={item} key={index} rateUnits={rateUnits} onChange={(name, value) => updateRow("fertilizers", index, name, value)} onRemove={() => removeRow("fertilizers", index)} />) : <div className="border border-dashed border-slate-300 bg-[#fafbf8] px-4 py-5 text-sm text-slate-600">No fertilizer applications added. Holos will calculate without fertilizer inputs.</div>}
              </div>
            </section>

            <section className="border-t border-slate-200 p-5 md:p-6">
              <SectionHeading title="Field operations" tags={["accepted"]} description="Operation, equipment, date, and fuel data are accepted by TMHOLOS and retained for future model support." action={<button className="inline-flex items-center gap-2 self-start rounded-[4px] border border-slate-400 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50" disabled={form.operations.length >= (capabilities?.maxOperations || 25)} onClick={() => setForm((current) => ({ ...current, operations: [...current.operations, { ...EMPTY_OPERATION }] }))} type="button"><AddOutlinedIcon fontSize="small" /> Add operation</button>} />
              <div className="mt-5 space-y-4">
                {form.operations.length ? form.operations.map((item, index) => <OperationRow index={index} item={item} key={index} onChange={(name, value) => updateRow("operations", index, name, value)} onRemove={() => removeRow("operations", index)} />) : <div className="border border-dashed border-slate-300 bg-[#fafbf8] px-4 py-5 text-sm text-slate-600">No field operations added.</div>}
              </div>
            </section>

            <section className="border-t border-slate-200 p-5 md:p-6">
              <SectionHeading title="Irrigation" tags={["modeled", "accepted"]} description="Applied water is normalized by Holos. Energy details are accepted and retained for future model support." action={<FormControlLabel control={<Switch checked={Boolean(form.irrigation)} color="success" onChange={(event) => setForm((current) => ({ ...current, irrigation: event.target.checked ? { ...EMPTY_IRRIGATION } : null }))} />} label={form.irrigation ? "Irrigated" : "Not irrigated"} />} />
              {form.irrigation ? <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <p className="text-sm text-slate-600 md:col-span-2 lg:col-span-3">{WATER_HELP}</p>
                <TextField required label="Water applied" type="number" value={form.irrigation.waterApplied} onChange={(event) => updateGroup("irrigation", "waterApplied", event.target.value)} inputProps={{ min: 0, max: 1e12, step: "any" }} />
                <TextField required select label="Water unit" value={form.irrigation.waterUnit} onChange={(event) => updateGroup("irrigation", "waterUnit", event.target.value)}>{waterUnits.filter(value => !["inch", "inches"].includes(value) || form.irrigation.waterUnit === value).map((value) => <MenuItem key={value} value={value}>{WATER_LABELS[value] || value}</MenuItem>)}</TextField>
                <TextField select label="Energy source" value={form.irrigation.energySource} onChange={(event) => setForm(current => ({ ...current, irrigation: energySourceChange(current.irrigation, event.target.value) }))}><MenuItem value="">Not recorded</MenuItem>{Object.keys(ENERGY_SOURCE_UNITS).map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
                <TextField label="Energy used" type="number" value={form.irrigation.energyUsed} onChange={(event) => updateGroup("irrigation", "energyUsed", event.target.value)} inputProps={{ min: 0, max: 10000000, step: "any" }} />
                <TextField select label="Energy unit" value={form.irrigation.energyUnit} onChange={(event) => updateGroup("irrigation", "energyUnit", event.target.value)}><MenuItem value="">Not recorded</MenuItem>{(ENERGY_SOURCE_UNITS[form.irrigation.energySource] || ENERGY_UNITS).map(value => <MenuItem key={value} value={value}>{value === "m3" ? "m³" : value}</MenuItem>)}</TextField>
                <p className="text-sm text-slate-600 md:col-span-2 lg:col-span-3">{ENERGY_HELP}</p>
              </div> : null}
            </section>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-[#fcfcfa] px-5 py-5 sm:flex-row sm:items-center sm:justify-between md:px-6">
              <p className="text-sm text-slate-500">Results and request data expire after {capabilities?.resultTtlHours || 24} hours.</p>
              <button className="inline-flex items-center justify-center gap-2 rounded-[4px] bg-[#237a43] px-5 py-3 text-sm font-bold text-white hover:bg-[#1c6236] disabled:cursor-not-allowed disabled:bg-slate-300" disabled={busy || !capabilities?.available} type="submit"><CalculateOutlinedIcon fontSize="small" /> {busy ? "Calculating..." : "Calculate emissions"}</button>
            </div>
          </form>
        ) : (
          <section className="mt-4 rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.05)] md:p-6">
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 md:flex-row md:items-start md:justify-between">
              <div><h2 className="text-xl font-black text-slate-900">CSV calculations</h2><p className="mt-2 max-w-[760px] text-sm leading-6 text-slate-600">The v2 template includes every TMHOLOS input. Repeatable fertilizer and operation records use JSON array cells so each calculation remains one row.</p></div>
              <button className="inline-flex items-center gap-2 rounded-[4px] border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50" onClick={() => downloadText(GHG_CSV_TEMPLATE, "ghg-quick-calculator-v2-template.csv", "text/csv")} type="button"><DownloadOutlinedIcon fontSize="small" /> Download template</button>
            </div>
            <label className="mt-5 flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-[8px] border border-dashed border-slate-400 bg-[#fafbf8] px-5 text-center hover:bg-slate-50"><UploadFileOutlinedIcon className="text-slate-500" /><span className="mt-2 text-sm font-bold text-slate-700">Select a populated CSV</span><span className="mt-1 text-xs text-slate-500">UTF-8 · 256 KiB maximum · 10 calculations</span><input accept=".csv,text/csv" className="sr-only" onChange={(event) => handleCsvFile(event.target.files?.[0])} type="file" /></label>
            {csvError ? <Alert className="mt-4" severity="error">{csvError}</Alert> : null}
            {csvRows.length ? <div className="mt-5 overflow-x-auto"><table className="w-full min-w-[780px] border-collapse text-left text-sm"><thead><tr className="border-b border-slate-300 text-xs uppercase text-slate-500"><th className="px-3 py-2">Row</th><th className="px-3 py-2">Reference</th><th className="px-3 py-2">Crop</th><th className="px-3 py-2">Location</th><th className="px-3 py-2">Inputs</th><th className="px-3 py-2">Validation</th></tr></thead><tbody>{csvRows.map((row) => <tr className="border-b border-slate-200" key={row.rowNumber}><td className="px-3 py-3">{row.rowNumber}</td><td className="px-3 py-3 font-semibold">{row.payload.clientReference}</td><td className="px-3 py-3">{row.payload.crop}</td><td className="px-3 py-3">{row.payload.location.provinceCode}{row.payload.location.latitude != null ? ` · ${row.payload.location.latitude}, ${row.payload.location.longitude}` : ""}</td><td className="px-3 py-3">{row.payload.fertilizers.length} fertilizer · {row.payload.operations.length} operation</td><td className={`px-3 py-3 ${row.errors.length ? "text-red-700" : "text-emerald-700"}`}>{row.errors.length ? row.errors.join("; ") : "Ready"}</td></tr>)}</tbody></table><div className="mt-5 flex justify-end"><button className="inline-flex items-center gap-2 rounded-[4px] bg-[#237a43] px-5 py-3 text-sm font-bold text-white disabled:bg-slate-300" disabled={busy || hasCsvErrors || !capabilities?.available} onClick={handleCsvSubmit} type="button"><CalculateOutlinedIcon fontSize="small" /> {busy ? "Processing rows..." : `Calculate ${csvRows.length} row${csvRows.length === 1 ? "" : "s"}`}</button></div></div> : null}
          </section>
        )}

        {message ? <Alert className="mt-5" severity={message.toLowerCase().includes("completed") ? "success" : "error"}>{message}</Alert> : null}

        {jobs.length ? <section className="mt-8"><div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between"><div><div className="text-xs font-bold uppercase text-[#237a43]">Transient results</div><h2 className="mt-1 text-2xl font-black text-slate-900">Calculation results</h2></div><div className="flex flex-wrap gap-2"><button className="inline-flex items-center gap-2 rounded-[4px] border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50" disabled={busy} onClick={clearResults} type="button"><DeleteSweepOutlinedIcon fontSize="small" /> Clear results</button>{completedJobs.length ? <button className="inline-flex items-center gap-2 rounded-[4px] border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50" onClick={() => downloadText(buildGhgResultsCsv(jobs), "ghg-quick-calculator-results.csv", "text/csv")} type="button"><DownloadOutlinedIcon fontSize="small" /> Export results CSV</button> : null}</div></div><div className="mt-4 grid grid-cols-1 gap-4">{jobs.map((job) => <JobResult job={job} key={job.jobId} onDownloadJson={downloadJob} />)}</div></section> : null}
      </div>
    </div>
  );
}
